import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Area, AreaChart, Bar, BarChart, CartesianGrid, Cell, Legend, Pie, PieChart, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import { AlertTriangle, PhoneCall } from "lucide-react";
import { get } from "@/lib/api";
import { KpiTile, PageHeader, Section } from "@/components/common/Layout";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge, ScoreBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { COUNTRIES, LANGUAGES, dt, money, pct, title, duration } from "@/lib/format";
import { SetupBanner } from "@/pages/Setup";

const PIE_COLORS = ["#059669", "#2563EB", "#D97706", "#E11D48", "#0284C7", "#7C3AED", "#94A3B8",
  "#10B981", "#F59E0B", "#64748B", "#334155"];

export default function Dashboard() {
  const [filters, setFilters] = useState({
    date_from: "", date_to: "", campaign_id: "", language: "", country: "", phone_number: "",
  });
  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));

  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", params],
    queryFn: () => get("/dashboard", params),
    refetchInterval: 10000,
  });
  const { data: campaigns } = useQuery({
    queryKey: ["campaign-options"],
    queryFn: () => get("/campaigns", { page_size: 100 }),
  });
  const { data: numbers } = useQuery({ queryKey: ["numbers"], queryFn: () => get("/phone-numbers") });

  const k = data?.kpis || {};
  const set = (key) => (value) => setFilters((f) => ({ ...f, [key]: value === "all" ? "" : value }));

  return (
    <>
      <PageHeader
        testId="dashboard-page"
        title="Operations dashboard"
        subtitle="Live performance of every outbound AI voice campaign"
        actions={
          <Button asChild className="bg-blue-600 hover:bg-blue-700" data-testid="dashboard-new-campaign">
            <Link to="/campaigns/new">New campaign</Link>
          </Button>
        }
      />

      <SetupBanner />

      <div className="panel grid grid-cols-1 gap-3 p-3 sm:grid-cols-2 lg:grid-cols-6">
        <Input
          data-testid="filter-date-from"
          type="date"
          value={filters.date_from}
          onChange={(e) => set("date_from")(e.target.value)}
          className="h-9"
        />
        <Input
          data-testid="filter-date-to"
          type="date"
          value={filters.date_to}
          onChange={(e) => set("date_to")(e.target.value)}
          className="h-9"
        />
        <Select value={filters.campaign_id || "all"} onValueChange={set("campaign_id")}>
          <SelectTrigger data-testid="filter-campaign" className="h-9">
            <SelectValue placeholder="Campaign" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All campaigns</SelectItem>
            {(campaigns?.items || []).map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.language || "all"} onValueChange={set("language")}>
          <SelectTrigger data-testid="filter-language" className="h-9">
            <SelectValue placeholder="Language" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All languages</SelectItem>
            {LANGUAGES.map((l) => (
              <SelectItem key={l.value} value={l.value}>{l.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.country || "all"} onValueChange={set("country")}>
          <SelectTrigger data-testid="filter-country" className="h-9">
            <SelectValue placeholder="Country" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All countries</SelectItem>
            {COUNTRIES.map((c) => (
              <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filters.phone_number || "all"} onValueChange={set("phone_number")}>
          <SelectTrigger data-testid="filter-number" className="h-9">
            <SelectValue placeholder="Phone number" />
          </SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All numbers</SelectItem>
            {(numbers || []).map((n) => (
              <SelectItem key={n.id} value={n.phone}>{n.phone}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 lg:grid-cols-5">
        <KpiTile testId="kpi-active-calls" label="Active calls" value={k.active_calls ?? "—"} tone="primary" icon={PhoneCall} />
        <KpiTile testId="kpi-calls-today" label="Calls today" value={k.calls_today ?? "—"} />
        <KpiTile testId="kpi-answered" label="Answered" value={k.answered_calls ?? "—"} tone="success" />
        <KpiTile testId="kpi-answer-rate" label="Human answer rate" value={pct(k.human_answer_rate)} />
        <KpiTile testId="kpi-voicemail-rate" label="Voicemail rate" value={pct(k.voicemail_rate)} tone="warning" />
        <KpiTile testId="kpi-interested" label="Interested prospects" value={k.interested ?? "—"} tone="success" />
        <KpiTile testId="kpi-callbacks" label="Callback requests" value={k.callback_requests ?? "—"} />
        <KpiTile testId="kpi-dnc" label="Do-not-call requests" value={k.dnc_requests ?? "—"} tone="danger" />
        <KpiTile testId="kpi-conversion" label="Conversion rate" value={pct(k.conversion_rate)} tone="success" />
        <KpiTile testId="kpi-spend" label="Total spend" value={money(k.total_spend)} />
        <KpiTile testId="kpi-cost-per-lead" label="Cost / interested lead" value={money(k.cost_per_interested)} />
        <KpiTile testId="kpi-avg-duration" label="Avg call duration" value={duration(k.avg_duration)} />
        <KpiTile testId="kpi-avg-interest" label="Avg interest score" value={`${k.avg_interest_score ?? 0}/10`} />
        <KpiTile testId="kpi-total-calls" label="Calls in range" value={k.total_calls ?? "—"} />
        <KpiTile testId="kpi-converted" label="Converted leads" value={k.converted_leads ?? "—"} tone="success" />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Section title="Calls and conversions" subtitle="Last 14 days" className="lg:col-span-2" testId="chart-calls">
          <div className="h-72 px-2 py-3">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.series || []}>
                <defs>
                  <linearGradient id="callsFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" stopOpacity={0.25} />
                    <stop offset="100%" stopColor="#2563EB" stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="calls" stroke="#2563EB" fill="url(#callsFill)" strokeWidth={2} />
                <Area type="monotone" dataKey="interested" stroke="#059669" fill="#05966922" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <Section title="Results distribution" testId="chart-outcomes">
          <div className="h-72 px-2 py-3">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={(data?.outcomes || []).map((o) => ({ name: title(o._id), value: o.count }))}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={45}
                  outerRadius={80}
                  paddingAngle={2}
                >
                  {(data?.outcomes || []).map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Section>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Section title="Performance by language" testId="chart-language">
          <div className="h-64 px-2 py-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={(data?.by_language || []).map((l) => ({
                name: (l._id || "n/a").toUpperCase(), calls: l.calls, answered: l.answered,
                interested: l.interested,
              }))}>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="calls" fill="#93C5FD" radius={[3, 3, 0, 0]} />
                <Bar dataKey="answered" fill="#2563EB" radius={[3, 3, 0, 0]} />
                <Bar dataKey="interested" fill="#059669" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Section>

        <Section title="Performance by campaign" testId="table-campaign-performance">
          <DataTable
            testId="campaign-performance-table"
            loading={isLoading}
            columns={[
              { key: "name", label: "Campaign", render: (r) => <span className="font-medium text-slate-800">{r._id?.name || "—"}</span> },
              { key: "calls", label: "Calls" },
              { key: "answered", label: "Answered" },
              { key: "interested", label: "Interested" },
              { key: "cost", label: "Cost", render: (r) => money(r.cost) },
            ]}
            rows={(data?.by_campaign || []).map((c, i) => ({ ...c, id: c._id?.id || i }))}
            empty={{ title: "No campaign activity in this range" }}
          />
        </Section>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Section title="Recent interested leads" className="lg:col-span-2" testId="table-recent-leads">
          <DataTable
            testId="recent-leads-table"
            loading={isLoading}
            columns={[
              { key: "contact_name", label: "Contact" },
              { key: "campaign_name", label: "Campaign" },
              { key: "product", label: "Product" },
              { key: "interest_score", label: "Score", render: (r) => <ScoreBadge score={r.interest_score} /> },
              { key: "status", label: "Status", render: (r) => <StatusBadge value={r.status} /> },
              { key: "created_at", label: "Created", render: (r) => dt(r.created_at) },
            ]}
            rows={data?.recent_leads || []}
            empty={{ title: "No interested leads yet", description: "Start a campaign to generate leads." }}
          />
        </Section>

        <div className="space-y-4">
          <Section title="Active campaigns" testId="active-campaigns">
            <div className="divide-y divide-slate-100">
              {(data?.active_campaigns || []).length === 0 ? (
                <p className="px-4 py-6 text-sm text-slate-500">No active campaigns.</p>
              ) : (
                data.active_campaigns.map((c) => (
                  <Link
                    key={c.id}
                    to={`/campaigns/${c.id}`}
                    data-testid={`active-campaign-${c.id}`}
                    className="flex items-center justify-between gap-3 px-4 py-2.5 transition-colors duration-150 hover:bg-slate-50"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-slate-800">{c.name}</p>
                      <p className="text-xs text-slate-500">
                        {c.calls_made} calls · {c.interested} interested
                      </p>
                    </div>
                    <StatusBadge value={c.status} />
                  </Link>
                ))
              )}
            </div>
          </Section>

          <Section title="Alerts" testId="alerts-panel">
            <div className="divide-y divide-slate-100">
              {(data?.alerts || []).length === 0 && (data?.number_warnings || []).length === 0 ? (
                <p className="px-4 py-6 text-sm text-slate-500">No cost or provider alerts.</p>
              ) : null}
              {(data?.alerts || []).map((a) => (
                <div key={a.id} className="flex gap-2 px-4 py-2.5">
                  <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${a.level === "destructive" ? "text-rose-500" : "text-amber-500"}`} />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{a.title}</p>
                    <p className="text-xs text-slate-500">{a.message}</p>
                  </div>
                </div>
              ))}
              {(data?.number_warnings || []).map((n) => (
                <div key={n.id} className="flex gap-2 px-4 py-2.5">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{n.phone}</p>
                    <p className="text-xs text-slate-500">
                      {n.spam_warning ? "Spam reputation warning" : `Number status: ${title(n.status)}`}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </Section>
        </div>
      </div>
    </>
  );
}